import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import i18n from "../../../i18n";

import { useAsync } from "../../../hooks/useAsync";
import LoaderWrapper from "../../wrapper/LoaderWrapper";

import useEquipmentService from "../../../services/equipmentService";

import CollapsibleTable from "../CollapsibleTable";
import TableService from "../../../services/tableService";
import FilterTable from "../FilterTable";

import EquipmentInstalationTenantSetUsageDialog from "../../dialog/EquipmentInstalationTenantSetUsageDialog";
import EquipmentInstalationTenantSetUsageForSchemeDialog from "../../dialog/EquipmentInstalationTenantSetUsageForSchemeDialog/EquipmentInstalationTenantSetUsageForSchemeDialog";

import useDialogWithId from "../../../hooks/useDialogWithId";

import {
  EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
  EQUIPMENT_USAGE_TABLE_NAME,
  EITS_TABLE_FIELDS_CONFIG,
  EITS_USAGE_TABLE_NAME,
  getEquipmentUsageMonthsLabelsMapping,
  getEitsUsageMonthsLabelsMapping,
} from "./TableConfigs";

const InnerEquipmentInstalationTenantSetUsageTable = React.memo(
  ({
    id,
    updatedInnerElementData,
    hiddenColumns,
    columnsOrders,
    usagePeriod,
    refreshEquipmentUsageTable,
    readOnly,
  }) => {
    const monthLabels = useMemo(
      () => getEitsUsageMonthsLabelsMapping(usagePeriod),
      [usagePeriod]
    );

    const [usageDataLocal, setUsageDataLocal] = useState(undefined);
    const { getEquipmentInstalationTenantSetUsageData } = useEquipmentService();

    const usageData = useAsync(
      () => getEquipmentInstalationTenantSetUsageData(`equipment_usage=${id}`),
      [updatedInnerElementData, id]
    );

    useEffect(() => {
      if (!usageData.loading) {
        setUsageDataLocal(usageData.value);
      }
    }, [usageData.loading]);

    const [
      openEitsUsageDialog,
      eitsUsageId,
      onOpenEitsUsageDialog,
      onCloseEitsUsageDialog,
    ] = useDialogWithId();

    const [
      openEitsUsageForSchemeDialog,
      eitsUsageForSchemeId,
      onOpenEitsUsageForSchemeDialog,
      onCloseEitsUsageForSchemeDialog,
    ] = useDialogWithId();

    const handleSubmitEitsUsageDialog = () => {
      if (refreshEquipmentUsageTable) {
        refreshEquipmentUsageTable();
      }
    };

    const tableConfig = React.useMemo(
      () =>
        readOnly
          ? EITS_TABLE_FIELDS_CONFIG.filter((config) => config.name != "edit")
          : EITS_TABLE_FIELDS_CONFIG,
      [readOnly]
    );

    const data = React.useMemo(
      () =>
        usageDataLocal
          ? TableService.getPreparedDataForCollapsibleTable(
            tableConfig.map((config) => {
              if (Object.keys(monthLabels).includes(config.name)) {
                config.label = `${i18n.t("usage")} ${monthLabels[config.name]
                  }`;
              }
              return config;
            }),
            usageDataLocal,
            hiddenColumns,
            columnsOrders,
            onOpenEitsUsageDialog,
            onOpenEitsUsageForSchemeDialog
          )
          : [],
      [
        usageDataLocal,
        hiddenColumns,
        columnsOrders,
        usagePeriod,
        onOpenEitsUsageDialog,
        onOpenEitsUsageForSchemeDialog,
        tableConfig,
      ]
    );

    return (
      <LoaderWrapper showLoader={usageData.loading}>
        <>
          {usageDataLocal && (
            <FilterTable
              data={data}
              showFilters={false}
              headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                tableConfig,
                undefined,
                hiddenColumns,
                columnsOrders
              )}
              style={{ maxHeight: "40vh" }}
              tableConfig={tableConfig}
              showCleanFilterIcon={false}
              showContextMenu={false}
              showCheckbox={false}
              withPagination={false}
              showTitle={false}
              showCounterRecords={false}
              isLoading={usageData.loading}
              showTableLoader={true}
            />
          )}
        </>
        {openEitsUsageDialog && (
          <EquipmentInstalationTenantSetUsageDialog
            open={openEitsUsageDialog}
            onClose={onCloseEitsUsageDialog}
            eitsUsageId={eitsUsageId}
            onSubmitCallback={handleSubmitEitsUsageDialog}
          />
        )}
        {openEitsUsageForSchemeDialog && (
          <EquipmentInstalationTenantSetUsageForSchemeDialog
            open={openEitsUsageForSchemeDialog}
            onClose={onCloseEitsUsageForSchemeDialog}
            eitsUsageId={eitsUsageForSchemeId}
            onSubmitCallback={handleSubmitEitsUsageDialog}
          />
        )}
      </LoaderWrapper>
    );
  }
);

const EquipmentUsageCollapsibleTable = React.memo((props) => {
  const monthLabels = useMemo(
    () => getEquipmentUsageMonthsLabelsMapping(props.usagePeriod),
    [props.usagePeriod]
  );

  const tableConfig = React.useMemo(
    () =>
      props.readOnly
        ? EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG.filter(
          (config) => config.name != "recalculate"
        )
        : EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
    [props.readOnly]
  );

  const data = React.useMemo(
    () =>
      TableService.getPreparedDataForCollapsibleTable(
        tableConfig.map((config) => {
          if (Object.keys(monthLabels).includes(config.name)) {
            config.label = `${i18n.t("usage")} ${monthLabels[config.name]}`;
          }
          return config;
        }),
        props.data,
        props.hiddenColumns[EQUIPMENT_USAGE_TABLE_NAME],
        props.columnsOrders[EQUIPMENT_USAGE_TABLE_NAME],
        props.onClickRecalculate
      ),
    [
      props.data,
      monthLabels,
      props.hiddenColumns[EQUIPMENT_USAGE_TABLE_NAME],
      props.columnsOrders[EQUIPMENT_USAGE_TABLE_NAME],
      props.onClickRecalculate,
      tableConfig,
    ]
  );
  return (
    <CollapsibleTable
      showChangesCircle={false}
      checkedAll={props.checkedAll}
      showCheckbox={props.showCheckbox}
      onClickCheck={props.onClickCheck}
      onClickCheckAll={props.onClickCheckAll}
      showDetailsIcon={false}
      showCleanFilterIcon={true}
      onClickRow={props.onClickRow}
      data={data}
      filteringData={props.filteringData}
      countRecords={props.countRecords}
      page={props.page}
      onPageChange={props.onPageChange}
      rowsPerPage={props.rowsPerPage}
      onRowsPerPageChange={props.onRowsPerPageChange}
      rowsPerPageOptions={props.rowsPerPageOptions}
      resetPageNumber={props.resetPageNumber}
      headersConfig={TableService.getHeadersConfigForCollapsibleTable(
        tableConfig,
        props.filteringData,
        props.hiddenColumns[EQUIPMENT_USAGE_TABLE_NAME],
        props.columnsOrders[EQUIPMENT_USAGE_TABLE_NAME]
      )}
      CollapsibleElement={
        <InnerEquipmentInstalationTenantSetUsageTable
          hiddenColumns={props.hiddenColumns[EITS_USAGE_TABLE_NAME]}
          columnsOrders={props.columnsOrders[EITS_USAGE_TABLE_NAME]}
          usagePeriod={props.usagePeriod}
          refreshEquipmentUsageTable={props.refreshEquipmentUsageTable}
          readOnly={props.readOnly}
        />
      }
      selectedItemId={props.selectedEquipmentUsageId}
      isSelectedOuterItem={props.isSelectedEquipmentUsage}
      selectedInnerItemId={props.selectedEitsUsageId}
      style={props.style}
      filterPrefix={props.filterPrefix}
      showExportToFileButton={props.showExportToFileButton}
      exportToFileUrl={props.exportToFileUrl}
      exportToFileSearchParams={props.exportToFileSearchParams}
      exportToFileHeaders={props.exportToFileHeaders}
      exportToFileFileName={props.exportToFileFileName}
        isLoading={props.isLoading}
        showTableLoader={props.showTableLoader}
    />
  );
});

EquipmentUsageCollapsibleTable.propTypes = {
  showDetailsIcon: PropTypes.bool,
  checkedAll: PropTypes.bool,
  onClickCheck: PropTypes.func,
  onClickCheckAll: PropTypes.func,
  onClickRow: PropTypes.func,
  showCleanFilterIcon: PropTypes.bool,
  data: PropTypes.array,
  filteringData: PropTypes.object,
  countRecords: PropTypes.number,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPageChange: PropTypes.func,
  rowsPerPage: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  resetPageNumber: PropTypes.func,
  hiddenColumns: PropTypes.object,
  columnsOrders: PropTypes.object,
  isSelectedEquipmentUsage: PropTypes.bool,
  selectedEquipmentUsageId: PropTypes.string,
  selectedEitsUsageId: PropTypes.string,
  style: PropTypes.object,
  filterPrefix: PropTypes.string,
  onClickInnerTableRow: PropTypes.func,
  showChangesCircle: PropTypes.func,
  showExportToFileButton: PropTypes.bool,
  exportToFileUrl: PropTypes.string,
  exportToFileSearchParams: PropTypes.object,
  exportToFileHeaders: PropTypes.array,
  exportToFileFileName: PropTypes.string,
  usagePeriod: PropTypes.string,
  refreshEquipmentUsageTable: PropTypes.func,
  onClickRecalculate: PropTypes.func,
};

EquipmentUsageCollapsibleTable.defaultProps = {
  hiddenColumns: {},
  columnsOrders: {},
  isSelectedEquipmentUsage: true,
  showDetailsIcon: false,
  showCleanFilterIcon: false,
  showChangesCircle: false,
};

export default EquipmentUsageCollapsibleTable;
